import { lazy, type ReactNode, Suspense, useContext, useEffect } from "react";
import {
  type HtmlLinkDescriptor,
  Links,
  type LinksFunction,
  type LoaderFunctionArgs,
  Meta,
  Outlet,
  Scripts,
  useLoaderData,
} from "react-router";

import { Language, SporProvider } from "@vygruppen/spor-react";

import { OpfsWorkerProvider } from "~/features/cache/opfs-context";
import { getDayPlanOverride } from "~/features/day-plan/cookie.server";
import { AbilityProvider } from "~/features/permissions/AbilityContext";
import { PushNotificationsModal } from "~/features/push-notifications/PushNotificationsModal";
import { PushSubscriptionProvider } from "~/features/push-notifications/PushSubscriptionContext";
import { ServiceWorkerUpdaterProvider } from "~/features/pwa/service-worker-updater";
import { useNavigationSync } from "~/features/pwa/use-navigation-sync";
import { PrefetchQueries } from "~/features/query-cache/PrefetchQueries";
import { QueryCacheProvider } from "~/features/query-cache/provider";
import { syncEmployeeId } from "~/features/storage/sync";
import { CurrentTimeProvider } from "~/features/time/context";
import globalStyles from "~/global.css?url";
import { vyTeknologiTheme } from "~/theme";
import { APP_TITLE } from "~/utils/constants";
import { RUN_WITH_MOCKS } from "~/utils/environment.server";
import { getResponsiveValue } from "~/utils/responsive";
import { withEnv } from "~/utils/string";
import { type Route } from "../.react-router/types/app/+types/root";
import { withResolvedRoles } from "./features/auth/utils";
import { useEnrichDatadogRUMGlobalContext } from "./features/logging/use-enrich-datadog-rum-global-context";
import { canUseFeature } from "./features/permissions/ability.server";
import { QueryUpdateContextProvider } from "./features/query-update/QueryUpdateContext";
import {
  ClientStyleContext,
  ServerStyleContext,
} from "./features/spor/style-context";
import { useIsTrainInsights } from "./features/train-insights/use-is-train-insights";
import { ClientOnly } from "./utils/client-only";
import { resolveEmployeeNumber } from "./utils/user";

import { datadogRum } from "@datadog/browser-rum";
import { withEmotionCache } from "@emotion/react";
import reactPdfCoreStyles from "@react-pdf-viewer/core/lib/styles/index.css?url";
import reactPdfDefaultStyles from "@react-pdf-viewer/default-layout/lib/styles/index.css?url";
import pdfSearchStyles from "@react-pdf-viewer/search/lib/styles/index.css?url";
import mapLibreStyles from "maplibre-gl/dist/maplibre-gl.css?url";
import { $path } from "safe-routes";
import { type Jsonify } from "type-fest";

export { RootErrorBoundary as ErrorBoundary } from "~/features/error-handling/RootErrorBoundary";

export const meta = ({ loaderData }: Route.MetaArgs) => {
  const appTitles = loaderData?.isTrainInsights
    ? APP_TITLE.trainInsights
    : APP_TITLE.topas;

  return [
    {
      title: withEnv(appTitles.short, loaderData?.env),
    },
    {
      charSet: "utf-8",
    },
    {
      name: "viewport",
      content: "width=device-width,initial-scale=1",
    },
    {
      name: "description",
      content: appTitles.long,
    },
    {
      name: "theme-color",
      content: "#00453E",
    },
    {
      name: "robots",
      content: "noindex, nofollow",
    },
    {
      httpEquiv: "Accept-CH",
      content: "Sec-CH-UA-Model, Sec-CH-UA-Platform-Version",
    },
  ];
};

export const links: LinksFunction = () => [
  {
    rel: "manifest",
    href: $path("/resources/manifest.webmanifest"),
  },
  ...[
    "VyDisplay-Medium",
    "VySans-Regular",
    "VySans-Light",
    "VySans-Bold",
  ].map<HtmlLinkDescriptor>((font) => ({
    rel: "preload",
    href: `https://www.vy.no/styles/font/${font}.woff2`,
    as: "font",
    crossOrigin: "anonymous",
    type: "font/woff2",
  })),
  {
    rel: "icon",
    href: "/favicon.svg",
    type: "image/svg+xml",
  },
  {
    rel: "stylesheet",
    href: globalStyles,
  },
  {
    rel: "stylesheet",
    href: pdfSearchStyles,
  },
  {
    rel: "stylesheet",
    href: mapLibreStyles,
  },
  {
    rel: "apple-touch-icon",
    href: "/icons/apple-icon-180x180.png",
    type: "image/png",
  },
  {
    rel: "apple-touch-icon-precomposed",
    href: "/icons/apple-icon-180x180.png",
    type: "image/png",
  },
  {
    rel: "stylesheet",
    href: reactPdfCoreStyles,
  },
  {
    rel: "stylesheet",
    href: reactPdfDefaultStyles,
  },
];

export async function loader({ request, context }: LoaderFunctionArgs) {
  const dayPlanOverride = await getDayPlanOverride(request, context);

  const cookieHeader = request.headers.get("Cookie") ?? "";
  const themeCookie = cookieHeader.match(/(?:^|;\s*)theme=([^;]+)/)?.[1];
  const colorMode: "light" | "dark" =
    themeCookie === "light" || themeCookie === "dark" ? themeCookie : "dark";

  // TODO: remove this override once roleFromEntraGroup is enabled for everyone
  // Wanted to do this in cognito-server instead, but can't access context easily there.
  // This function bases roles isConductor/isDriver on new Entra AD groups if flag is turned on,
  // otherwise fall back to old user.position-based function.
  const canUseRoleFromEntraGroup = canUseFeature(context, "roleFromEntraGroup");
  const user =
    context.session?.user &&
    withResolvedRoles(context.session.user, canUseRoleFromEntraGroup);

  return {
    colorMode,
    breakpoint: getResponsiveValue(request, {
      mobile: "base",
      tablet: "sm",
      desktop: "md",
    }),
    user,
    dayPlanOverride,
    featureToggleOverride: context.featureToggleOverride,
    vapidPublicKey: process.env.VAPID_PUBLIC_KEY,
    appConfig: context.appConfig,
    env: process.env.VY_ENV,
    version: process.env.VERSION,
    datadogConfig: {
      applicationId: process.env.DATADOG_APPLICATION_ID,
      clientToken: process.env.DATADOG_CLIENT_TOKEN,
      service: process.env.DATADOG_SERVICE,
    },
    devToolsConfig: !!user?.rbac?.isDeveloper && {
      hasTanstackQueryDevTools: true,
      hasFeatureToggleDevTools: true,
      hasDayPlanOverride: true,
    },
    slackFeedbackChannelId: process.env.FEEDBACK_SLACK_CHANNEL_ID,
    isMocking: RUN_WITH_MOCKS,
    isTrainInsights: process.env.TRAIN_INSIGHTS === "true",
  };
}

export type RootLoaderData = Jsonify<Awaited<ReturnType<typeof loader>>>;

type DocumentProps = {
  children: ReactNode;
  isAuthenticated: boolean;
  env?: string;
  datadogConfig?: RootLoaderData["datadogConfig"];
};

const Document = withEmotionCache(
  (
    { children, isAuthenticated, env, datadogConfig }: DocumentProps,
    emotionCache,
  ) => {
    const serverStyleData = useContext(ServerStyleContext);
    const clientStyleData = useContext(ClientStyleContext);

    useEffect(() => {
      emotionCache.sheet.container = document.head;
      const tags = emotionCache.sheet.tags;
      emotionCache.sheet.flush();
      for (const tag of tags) {
        // @ts-expect-error - Typed as private, but we can actually use it
        emotionCache.sheet?._insertTag(tag);
      }

      clientStyleData.reset();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      // "no" as in norwegian and "no" as in "don't translate this page"
      <html lang="no" translate="no" suppressHydrationWarning>
        <head>
          <Meta />
          <Links />
          {serverStyleData?.map(({ key, ids, css }) => (
            <style
              key={key}
              data-emotion={`${key} ${ids.join(" ")}`}
              dangerouslySetInnerHTML={{ __html: css }}
            />
          ))}
          {env && (
            <script
              dangerouslySetInnerHTML={{
                __html: `window.__vyEnv=${JSON.stringify(env)};`,
              }}
            />
          )}
          {datadogConfig && (
            <script
              dangerouslySetInnerHTML={{
                __html: `window.__datadogConfig=${JSON.stringify(datadogConfig)};`,
              }}
            />
          )}
        </head>
        <body>
          <SporProvider
            language={Language.NorwegianBokmal}
            theme={vyTeknologiTheme}
          >
            <QueryCacheProvider>
              <AbilityProvider>
                <PushSubscriptionProvider>
                  <ServiceWorkerUpdaterProvider
                    isAuthenticated={isAuthenticated}
                  >
                    <QueryUpdateContextProvider>
                      <CurrentTimeProvider>{children}</CurrentTimeProvider>
                    </QueryUpdateContextProvider>
                  </ServiceWorkerUpdaterProvider>
                </PushSubscriptionProvider>
              </AbilityProvider>
            </QueryCacheProvider>
          </SporProvider>
          <Scripts />
        </body>
      </html>
    );
  },
);

const DevToolsProvider = lazy(() =>
  import("~/features/dev-tools/DevToolsProvider").then((m) => ({
    default: m.DevToolsProvider,
  })),
);

export default function App() {
  const {
    user,
    dayPlanOverride,
    devToolsConfig,
    isMocking,
    env,
    datadogConfig,
  } = useLoaderData<RootLoaderData>();

  const { isTrainInsights } = useIsTrainInsights();

  useNavigationSync(!!user);
  useEnrichDatadogRUMGlobalContext();

  useEffect(() => {
    void syncEmployeeId(
      resolveEmployeeNumber(user?.employeeId, dayPlanOverride),
    );
  }, [dayPlanOverride, user?.employeeId]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const role = user.rbac.isConductor
      ? "conductor"
      : user.rbac.isDriver
        ? "driver"
        : "other";

    datadogRum.setUser({
      id: user.employeeId,
      role,
    });
  }, [user]);

  if (isMocking) {
    console.log("Skip prefetching queries in mock mode (for now)");
  }

  return (
    <Document isAuthenticated={!!user} env={env} datadogConfig={datadogConfig}>
      <OpfsWorkerProvider>
        {!!user && !isMocking && !isTrainInsights && <PrefetchQueries />}
        {user?.employeeId && (
          <PushNotificationsModal employeeId={user.employeeId} />
        )}
        <Outlet />
        {devToolsConfig && (
          <ClientOnly>
            {() => (
              <Suspense fallback={null}>
                <DevToolsProvider devToolsConfig={devToolsConfig} />
              </Suspense>
            )}
          </ClientOnly>
        )}
      </OpfsWorkerProvider>
    </Document>
  );
}
